import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Action, roleAllows } from '../permissions';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

/**
 * The signed-in user's role, and what it lets them see.
 *
 * The ID token carries `is_system_admin` and `organization_id` but **no role claim**, so the role
 * package has to come from the API: `GET /User/{ownId}` returns it as `rolePackageName`, and that
 * call is one every role may make (other users' ids come back 404). `init()` is awaited during
 * bootstrap, after `AuthService.init()`, so the first route activation already knows the answer and
 * the nav never renders a link it is about to take away.
 *
 * Two deliberate asymmetries:
 * - **A system administrator skips the fetch.** The claim alone grants everything, so their startup
 *   costs no extra request and cannot be broken by a failing `/User` call.
 * - **A failed fetch grants nothing.** Guessing generously would render an Administration page that
 *   then 403s on every request, which is the exact failure this is meant to prevent. The backend is
 *   the real gate, so under-granting costs a user a page they can reload their way back into; the
 *   other direction costs them a screen full of errors.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly auth    = inject(AuthService);
  private readonly userSvc = inject(UserService);

  /** The backend's English role package name, or null until resolved — or if it could not be. */
  readonly roleName = signal<string | null>(null);

  /** True once `init()` has finished, however it finished. Lets a caller tell "no" from "not yet". */
  readonly resolved = signal(false);

  /** System administrators are granted every capability without consulting the role map. */
  readonly isSystemAdmin = computed(() => this.auth.claims().isSystemAdmin);

  /**
   * Resolve the current user's role. Never rejects: a failure leaves `roleName` null, which grants
   * nothing, and the app boots regardless — this must not be able to block sign-in.
   */
  async init(): Promise<void> {
    if (this.isSystemAdmin()) {
      this.resolved.set(true);
      return;
    }
    const userId = this.auth.claims().userId;
    if (userId === null) {
      this.resolved.set(true);
      return;
    }
    try {
      const user = await firstValueFrom(this.userSvc.getById(userId));
      this.roleName.set(user.rolePackageName);
    } catch {
      // Left unresolved on purpose — see the class comment. No console noise: the failing request is
      // already visible in the network panel, and `no-console` allows only warn/error anyway.
    } finally {
      this.resolved.set(true);
    }
  }

  /**
   * Whether the signed-in user may perform `action`.
   *
   * The system-administrator bypass lives here rather than in the map because it comes from the ID
   * token's `is_system_admin` claim, not from the role name — and it is what makes the handful of
   * SystemAdmin-only operations (creating a device or an organization, changing a device's type or an
   * organization's service level) reachable for exactly one role.
   */
  can(action: Action): boolean {
    return this.isSystemAdmin() || roleAllows(this.roleName(), action);
  }
}
